export interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  type: CalendarEventType;
  source: CalendarSource;
  metadata?: Record<string, unknown>;
}

export type CalendarEventType = 'task' | 'time-entry' | 'milestone' | 'external' | 'native';

export type CalendarSource = 'internal' | 'google' | 'microsoft' | 'ical' | 'khronox';

export type CalendarView = 'month' | 'week' | 'work_week' | 'day' | 'agenda';

/**
 * Filter/legend categories used by the toolbar and the event cards — one
 * level coarser than `CalendarEventType`/`CalendarSource`: Google and
 * Microsoft are split (distinct providers, distinct colors), while
 * `milestone`/`time-entry` (both `source: 'internal'`, non-task) collapse
 * into a single "internal" bucket ("Eventos (Khronox)").
 */
export type CalendarFilterCategory = 'task' | 'google' | 'microsoft' | 'internal' | 'native';

export function resolveCalendarFilterCategory(event: CalendarEvent): CalendarFilterCategory {
  if (event.type === 'task') return 'task';
  if (event.source === 'google') return 'google';
  if (event.source === 'microsoft') return 'microsoft';
  if (event.source === 'khronox') return 'native';
  return 'internal';
}

export const CALENDAR_FILTER_CATEGORY_COLORS: Record<CalendarFilterCategory, string> = {
  task: 'bg-blue-500',
  google: 'bg-emerald-500',
  microsoft: 'bg-red-500',
  internal: 'bg-violet-500',
  native: 'bg-amber-500',
};

export interface DateRange {
  start: string; // ISO date
  end: string;
}

/** Tipos que el usuario puede crear directamente desde el calendario */
export type CreatableCalendarEventType = 'time-entry' | 'milestone';

/**
 * Discriminated date shape shared with external-provider events
 * (`RawExternalCalendarEventDate`) — `date` when `allDay: true`, `instant`
 * (+ optional `timeZoneId`) when `allDay: false`.
 */
export interface CalendarEventDateInput {
  instant?: string;
  timeZoneId?: string;
  date?: string;
}

export type CalendarEventModality = 'virtual' | 'in-person' | 'hybrid';

export type CalendarEventAttendeeResponseStatus =
  | 'needsaction'
  | 'accepted'
  | 'declined'
  | 'tentative';

/** A single attendee on a calendar event — present for all sources (internal/external/native). */
export interface CalendarEventAttendee {
  /** Platform user id — `null` for external-provider attendees with no matching platform user. */
  userId: string | null;
  email: string;
  displayName: string;
  isOrganizer: boolean;
  isOptional: boolean;
  responseStatus: CalendarEventAttendeeResponseStatus;
}

export interface CreateCalendarEventPayload {
  title: string;
  description?: string | null;
  location?: string | null;
  type: CreatableCalendarEventType;
  modality?: CalendarEventModality | null;
  allDay: boolean;
  start: CalendarEventDateInput;
  end: CalendarEventDateInput;
  /** Platform user ids as real numbers — the API's `Optional<T>` string-id convention does NOT apply here. */
  attendeeUserIds?: number[] | null;
}

export interface UpdateCalendarEventPayload {
  title?: string;
  description?: string | null;
  location?: string | null;
  type?: CreatableCalendarEventType;
  modality?: CalendarEventModality | null;
  allDay?: boolean;
  start?: CalendarEventDateInput;
  end?: CalendarEventDateInput;
  attendeeUserIds?: number[] | null;
}

// ── External calendar connections (Google / Microsoft) ──────────────────────
// PascalCase enums match the backend contract exactly (.NET Enum.ToString()).

export type CalendarProvider = 'Google' | 'Microsoft';

export type ConnectionStatus = 'Active' | 'NeedsReauth' | 'RevokedByProvider' | 'Disabled';

export type LinkedCalendarScope = 'Global' | 'Tenant';

export interface LinkedCalendar {
  id: string;
  providerCalendarId: string;
  name: string;
  description: string | null;
  colorHex: string | null;
  isPrimary: boolean;
  timeZoneId: string; // IANA, ej. "America/Mexico_City"
  scope: LinkedCalendarScope;
  tenantId: string | null; // presente solo si scope === 'Tenant'
  syncEnabled: boolean;
  lastIncrementalSyncAt: string | null; // ISO datetime
}

export interface CalendarConnection {
  id: string;
  provider: CalendarProvider;
  providerAccountEmail: string;
  status: ConnectionStatus;
  lastSyncedAt: string | null; // ISO datetime
  lastSyncError: string | null;
  calendars: LinkedCalendar[];
}

export interface UpdateLinkedCalendarPayload {
  scope: LinkedCalendarScope;
  tenantId: string | null;
  syncEnabled: boolean;
  colorHex: string | null;
}

export interface CalendarEventsSyncedPayload {
  connectionId: string;
  linkedCalendarId: string;
  upsertedCount: number;
  deletedCount: number;
}
